import { Menu } from "antd";
import React from "react";
import { Link, useRouteMatch } from "react-router-dom";
import styled from "styled-components";

import { COLORS } from "../../constants";
import { useTrack } from "../../utils";

import { TabName } from "./WorkspaceAdmin";

const StyledMenu = styled(Menu)`
  flex: 1;
  border-bottom: none;
  line-height: 36px;

  && .ant-menu-item {
    margin: 0px 32px 0 0 !important;
    padding: 0px 0px !important;
    font-weight: bold;

    &.ant-menu-item-selected {
      color: ${COLORS.coeblue4}!important;
      ::after {
        border-color: inherit !important;
      }
    }

    &:hover {
      color: ${COLORS.coeblue4}!important;
      ::after {
        border-color: inherit !important;
      }
    }

    ::after {
      border-color: transparent;
      right: 0px !important;
      left: 0px !important;
    }

    span: {
      color: inherit !important;
    }

    a {
      transition: none;
      color: inherit !important;
      padding: 0 !important;
    }

    &:hover {
      a {
        opacity: 0.8;
      }
    }

    svg {
      position: relative;
      top: 5px;
      margin-right: 10px;
    }
  }
`;

type Props = {
  baseUrl: string;
  domain: string;
};

export const WorkspaceAdminTabs: React.FC<Props> = ({ baseUrl, domain }) => {
  const track = useTrack();
  const routeMatch = useRouteMatch<{ tabName: TabName }>(`${baseUrl}/:tabName`);

  const activeTab = routeMatch?.params.tabName ?? TabName.users;

  return (
    <StyledMenu
      onClick={({ key }) => {
        if (key) {
          const tabMap: Record<TabName, string> = {
            imports: "workspace_admin_imports_list_viewed",
            runs: "workspace_admin_run_history_viewed",
            alerts: "workspace_admin_alerts_list_viewed",
            web_dashboards: "workspace_admin_web_dashboards_list_viewed",
            users: "workspace_admin_users_list_viewed",
            writebacks: "workspace_admin_writebacks_viewed",
            writeback_runs: "workspace_admin_writeback_runs_viewed",
            data_sources: "workspace_admin_data_sources_list_viewed",
            settings: "workspace_admin_settings_viewed",
          };

          track(tabMap[key], {
            event_from: "workspace_admin",
            domain: domain,
            platform: "webapp",
          });
        }
      }}
      selectedKeys={activeTab ? [activeTab] : []}
      mode="horizontal"
    >
      <Menu.Item key={TabName.users}>
        <Link to={`${baseUrl}/users?domain=${domain}`}>Users</Link>
      </Menu.Item>

      <Menu.Item key={TabName.data_sources}>
        <Link to={`${baseUrl}/data_sources?domain=${domain}`}>Data Sources</Link>
      </Menu.Item>

      <Menu.Item key={TabName.imports}>
        <Link to={`${baseUrl}/imports?domain=${domain}`}>Imports</Link>
      </Menu.Item>

      <Menu.Item key={TabName.runs}>
        <Link to={`${baseUrl}/runs?domain=${domain}`}>Import Run History</Link>
      </Menu.Item>

      <Menu.Item key={TabName.writebacks}>
        <Link to={`${baseUrl}/writebacks?domain=${domain}`}>Exports</Link>
      </Menu.Item>

      <Menu.Item key={TabName.writeback_runs}>
        <Link to={`${baseUrl}/writeback_runs?domain=${domain}`}>Export Run History</Link>
      </Menu.Item>

      <Menu.Item key={TabName.alerts}>
        <Link to={`${baseUrl}/alerts?domain=${domain}`}>Alerts</Link>
      </Menu.Item>

      {/* TODO(follow-up): the Dashboards tab should be paid-domains-only (per spec); add the
          paid-domain check alongside the Part 3 limit-enforcement work (plan/subscription data). */}
      <Menu.Item key={TabName.web_dashboards}>
        <Link to={`${baseUrl}/web_dashboards?domain=${domain}`}>Dashboards</Link>
      </Menu.Item>

      <Menu.Item key={TabName.settings}>
        <Link to={`${baseUrl}/settings`}>Settings</Link>
      </Menu.Item>
    </StyledMenu>
  );
};
